import { useMemo } from 'react';
import { FloatingLabel, Form } from 'react-bootstrap';

export interface EditSelectOption<V> {
  readonly key: string | number;
  readonly value: V;
  readonly label: string;
}

export interface EditSelectGroup<V> {
  readonly key: string | number;
  readonly label: string;
  readonly options: readonly EditSelectOption<V>[];
}

export type EditSelectOptionList<V> = readonly (EditSelectOption<V> | EditSelectGroup<V>)[];

interface EditSelectProps<V> {
  /** Floating label. Omit to render a bare control (the caller supplies its own label, e.g. via `Field`). */
  readonly label?: string;
  readonly value: V;
  readonly onChange: (value: V) => void;
  readonly mandatory?: boolean;
  readonly options: EditSelectOptionList<V>;
}

const isGroup = <V,>(item: EditSelectOption<V> | EditSelectGroup<V>): item is EditSelectGroup<V> => 'options' in item;

/**
 * Dropdown wrapper around `Form.Select` (wrapped in a `FloatingLabel`
 * when `label` is given; a bare control otherwise). Options carry
 * their own `key` (used as both the React key and the DOM `<option
 * value>`) and a `value` that's handed back to `onChange` on
 * selection. Groups are valid items in the options list — flat
 * `<option>`s and `<optgroup>`s can interleave.
 *
 * Nullability is the caller's concern: include `null` (or any sentinel
 * "no selection" value) in `V` and prepend an option that holds it.
 * Using `key: ''` for that option keeps HTML `required` validation
 * working (the browser treats `value=""` as no selection).
 */
export const EditSelect = <V,>({ label, value, onChange, mandatory = false, options }: EditSelectProps<V>) => {
  // Flatten once per render so both the controlled-value lookup
  // (V → DOM key) and the change handler (DOM key → V) share the
  // same key/value pairs.
  const flatOptions = useMemo(() => {
    const out: EditSelectOption<V>[] = [];
    for (const item of options) {
      if (isGroup(item)) out.push(...item.options);
      else out.push(item);
    }
    return out;
  }, [options]);

  const keyToValue = useMemo(() => new Map(flatOptions.map((o) => [String(o.key), o.value])), [flatOptions]);

  const currentDomKey = flatOptions.find((o) => o.value === value)?.key ?? '';

  const control = (
    <Form.Select
      value={String(currentDomKey)}
      onChange={(e) => {
        const k = e.target.value;
        if (keyToValue.has(k)) onChange(keyToValue.get(k) as V);
      }}
      required={mandatory}
    >
      {options.map((item) =>
        isGroup(item) ? (
          <optgroup key={item.key} label={item.label}>
            {item.options.map((opt) => (
              <option key={opt.key} value={String(opt.key)}>
                {opt.label}
              </option>
            ))}
          </optgroup>
        ) : (
          <option key={item.key} value={String(item.key)}>
            {item.label}
          </option>
        )
      )}
    </Form.Select>
  );

  return label ? <FloatingLabel label={mandatory ? `${label} *` : label}>{control}</FloatingLabel> : control;
};
